import type { ReactElement } from "react";
import { defineHFComponent } from "../component.js";

export type DataChartDatum = {
  label: string;
  revenue: number;
  conversion: number;
};

export type DataChartProps = {
  title?: string;
  subtitle?: string;
  source?: string;
  data?: DataChartDatum[];
};

export const DEFAULT_CHART_DATA: DataChartDatum[] = [
  { label: "Jan", revenue: 8, conversion: 2.1 },
  { label: "Feb", revenue: 12, conversion: 2.8 },
  { label: "Mar", revenue: 15, conversion: 3.2 },
  { label: "Apr", revenue: 11, conversion: 2.9 },
  { label: "May", revenue: 18, conversion: 3.8 },
  { label: "Jun", revenue: 22, conversion: 4.2 },
];

function chartGeometry(data: DataChartDatum[]) {
  const startX = 100;
  const startY = 600;
  const chartWidth = 1400;
  const chartHeight = 500;
  const barWidth = 80;
  const spacing = (chartWidth - barWidth * data.length) / (data.length + 1);
  const x = (i: number) => startX + spacing + i * (barWidth + spacing) + barWidth / 2;
  const barY = (value: number) => startY - (value / 25) * chartHeight;
  const lineY = (value: number) => startY - (value / 5) * chartHeight;
  return { startX, startY, chartWidth, chartHeight, barWidth, x, barY, lineY };
}

function View({
  title = "Monthly Revenue vs. Conversion Rate",
  subtitle = "Jan–Jun 2024, in thousands",
  source = "Source: Internal analytics",
  data = DEFAULT_CHART_DATA,
}: DataChartProps): ReactElement {
  const g = chartGeometry(data);
  const points = data.map((datum, index) => `${g.x(index)},${g.lineY(datum.conversion)}`).join(" L ");
  return (
    <><link
      href="https://fonts.googleapis.com/css2?family=Libre+Baskerville:wght@400;700&family=Libre+Franklin:wght@300;400;600&display=block"
      rel="stylesheet"
    /><div className="chart-container">
      <header className="chart-header">
        <h1>{title}</h1>
        <p>{subtitle}</p>
        <div className="chart-key">
          <span><i className="revenue-key" />Revenue</span>
          <span><i className="conversion-key" />Conversion Rate</span>
        </div>
      </header>
      <svg className="chart-svg" viewBox="0 0 1600 700" preserveAspectRatio="xMidYMid meet">
        <g className="gridlines">
          {[1, 2, 3, 4, 5].map((index) => {
            const y = g.startY - index * (g.chartHeight / 5);
            return <line className="grid-line" x1={g.startX} y1={y} x2={g.startX + g.chartWidth} y2={y} key={index} />;
          })}
        </g>
        <g className="bars-group">
          {data.map((datum, index) => {
            return <rect className="bar" x={g.x(index) - g.barWidth / 2} y={g.startY} width={g.barWidth} height={0} key={datum.label} />;
          })}
        </g>
        <g className="bar-labels-group">
          {data.map((datum, index) => (
            <text className="bar-label" x={g.x(index)} y={g.barY(datum.revenue) - 10} key={datum.label}>${datum.revenue}K</text>
          ))}
        </g>
        <path className="conversion-path" d={`M ${points}`} pathLength="1" />
        <circle className="line-dot" cx={g.x(0)} cy={g.lineY(data[0]?.conversion ?? 0)} r="5" />
        <g className="line-labels-group">{data.map((datum, index) => <text className="line-label" x={g.x(index)} y={g.lineY(datum.conversion) - 15} key={datum.label}>{datum.conversion.toFixed(1)}%</text>)}</g>
        <g className="axis-labels-group">{data.map((datum, index) => <text className="axis-label" x={g.x(index)} y={g.startY + 30} key={datum.label}>{datum.label}</text>)}</g>
        <line className="baseline" x1={g.startX} y1={g.startY} x2={g.startX + g.chartWidth} y2={g.startY} />
      </svg>
      <div className="chart-source">{source}</div>
    </div></>
  );
}

export const DataChart = defineHFComponent<DataChartProps>({
  name: "data-chart",
  View,
  css: (scope) => `
    ${scope}{position:absolute;inset:0;display:flex;align-items:center;justify-content:center;overflow:hidden;background:#faf9f6;color:#333;font-family:"Libre Franklin",sans-serif}
    ${scope} .chart-container{position:relative;width:1600px;height:900px;display:flex;flex-direction:column;transform:scale(1.15);transform-origin:center}
    ${scope} .chart-header{margin-bottom:40px;text-align:left}
    ${scope} h1{margin:0;padding:0;font-family:"Libre Baskerville",serif;font-size:42px;white-space:nowrap;clip-path:inset(0 100% 0 0)}
    ${scope} p{margin:8px 0 16px;color:#666;font-size:16px;white-space:nowrap;clip-path:inset(0 100% 0 0)}
    ${scope} .chart-key{display:flex;gap:24px;color:#666;font-size:13px;opacity:0}
    ${scope} .chart-key span{display:flex;align-items:center;gap:8px}
    ${scope} .chart-key i{display:block}
    ${scope} .revenue-key{width:10px;height:10px;background:#5c5c5c}
    ${scope} .conversion-key{width:16px;height:1.5px;background:#326fa8}
    ${scope} .chart-svg{width:100%;height:600px;overflow:visible}
    ${scope} .grid-line{stroke:#e8e8e8;stroke-width:.5;opacity:0}
    ${scope} .baseline{stroke:#111;stroke-width:1}
    ${scope} .bar{fill:#5c5c5c}
    ${scope} .conversion-path{fill:none;stroke:#326fa8;stroke-width:1.5;stroke-dasharray:1;stroke-dashoffset:1}
    ${scope} text{font-family:Inter,Arial,sans-serif;font-size:14px;text-anchor:middle}
    ${scope} .axis-label{fill:#666}
    ${scope} .bar-label{fill:#333;opacity:0}
    ${scope} .line-label{fill:#326fa8;font-weight:600;opacity:0}
    ${scope} .line-dot{fill:#326fa8;opacity:0}
    ${scope} .chart-source{position:absolute;bottom:0;left:0;color:#999;font-size:11px;opacity:0}
  `,
  timeline: ({ tl, select }, { data = DEFAULT_CHART_DATA }) => {
    tl.to(select(" h1"), { clipPath: "inset(0 0% 0 0)", duration: 1.2, ease: "power1.inOut" }, 0);
    tl.to(select(" .chart-header p"), { clipPath: "inset(0 0% 0 0)", duration: 1.2, ease: "power1.inOut" }, 0.2);
    tl.to(select(" .chart-key"), { opacity: 1, duration: 0.4, ease: "power2.out" }, 1);
    tl.to(select(" .grid-line"), { opacity: 1, duration: 0.5, stagger: 0.25, ease: "power2.out" }, 0.5);
    data.forEach((_, index) => {
      const at = 1.5 + index * 0.5;
      const geometry = chartGeometry(data);
      const y = geometry.barY(data[index]!.revenue);
      tl.to(select(` .bar:nth-child(${index + 1})`), { attr: { height: geometry.startY - y, y }, duration: 0.8, ease: "power2.out" }, at);
      tl.to(select(` .bar-labels-group .bar-label:nth-child(${index + 1})`), { opacity: 1, duration: 0.3, ease: "power2.out" }, at + 0.8);
    });
    const lineStart = 4.5;
    tl.to(select(" .conversion-path"), { strokeDashoffset: 0, duration: 3, ease: "none" }, lineStart);
    tl.to(select(" .line-dot"), { opacity: 1, duration: 0 }, lineStart);
    data.forEach((datum, index) => {
      if (index > 0) tl.to(select(" .line-dot"), { attr: { cx: chartGeometry(data).x(index), cy: chartGeometry(data).lineY(datum.conversion) }, duration: 0.6, ease: "none" }, lineStart + (index - 1) * 0.6);
      const labelAt = lineStart + index * 0.6 + 0.2;
      tl.to(select(` .line-labels-group .line-label:nth-child(${index + 1})`), { opacity: 1, duration: 0.1 }, labelAt);
    });
    tl.to(select(" .chart-source"), { opacity: 1, duration: 0.5, ease: "power2.out" }, 8.6);
  },
});
